import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Upload,
  Space,
  Tag,
  Switch
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { builtinAssets, upload, proxyImageUrl } from '../../api';

const ASSET_TYPE_OPTIONS = [
  { label: 'Logo', value: 'logo' },
  { label: '二维码', value: 'qrcode' },
  { label: '其他', value: 'other' }
];

const ASSET_TYPE_COLOR = {
  logo: 'blue',
  qrcode: 'green',
  other: 'default'
};
const ADMIN_ASSET_THUMBNAIL_OPTIONS = { width: 360, height: 300, mode: 'lfit', quality: 82 };

function BuiltinAssetManage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await builtinAssets.list({ include_disabled: 1 });
      setData(res.data.data || []);
    } catch (err) {
      message.error('获取内置素材失败');
    }

    setLoading(false);
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ type: 'logo', sort_order: 0, enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await builtinAssets.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleEnabledChange = async (record, enabled) => {
    try {
      await builtinAssets.update(record.id, {
        ...record,
        enabled
      });
      message.success(enabled ? '已启用' : '已停用');
      fetchData();
    } catch (err) {
      message.error('状态更新失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        await builtinAssets.update(editingItem.id, values);
        message.success('更新成功');
      } else {
        await builtinAssets.create(values);
        message.success('添加成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleUpload = async (file) => {
    try {
      const res = await upload.upload(file);
      form.setFieldsValue({ image_url: res.data.data.url });
      message.success('上传成功');
    } catch (err) {
      message.error('上传失败');
    }

    return false;
  };

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>内置素材库</div>
            <div style={{ color: '#667085' }}>
              后台上传常用 logo、二维码或其他装饰素材，前台编辑页可直接插入使用。
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: 160 }}>
              添加内置素材
            </Button>
          </Space>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
          {data.map((item) => (
            <Card
              key={item.id}
              loading={loading}
              hoverable
              cover={
                <div style={{ height: 200, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                  <img
                    alt={item.name}
                    src={proxyImageUrl(item.image_url, ADMIN_ASSET_THUMBNAIL_OPTIONS)}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
              }
              actions={[
                <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
                  编辑
                </Button>,
                <Popconfirm key="delete" title="确定删除这个素材？" onConfirm={() => handleDelete(item.id)}>
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              ]}
            >
              <Card.Meta
                title={item.name}
                description={
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <div>
                      <Tag color={ASSET_TYPE_COLOR[item.type] || 'default'}>
                        {ASSET_TYPE_OPTIONS.find((option) => option.value === item.type)?.label || '其他'}
                      </Tag>
                      <Tag color={item.enabled === false ? 'default' : 'success'}>
                        {item.enabled === false ? '已停用' : '已启用'}
                      </Tag>
                    </div>
                    <Switch
                      checked={item.enabled !== false}
                      checkedChildren="启用"
                      unCheckedChildren="停用"
                      onChange={(checked) => handleEnabledChange(item, checked)}
                    />
                    <div style={{ color: '#98a2b3', wordBreak: 'break-all' }}>{item.image_url}</div>
                  </Space>
                }
              />
            </Card>
          ))}
        </div>

        {!data.length && !loading && (
          <Card>
            <div style={{ textAlign: 'center', color: '#98a2b3', padding: '36px 0' }}>还没有内置素材，先上传几个常用 logo 或二维码。</div>
          </Card>
        )}
      </Space>

      <Modal
        title={editingItem ? '编辑内置素材' : '添加内置素材'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={640}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="素材名称" rules={[{ required: true, message: '请输入素材名称' }]}>
            <Input placeholder="例如：瑞月山白色 logo" />
          </Form.Item>

          <Form.Item name="type" label="素材类型" rules={[{ required: true, message: '请选择素材类型' }]}>
            <Select options={ASSET_TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item name="sort_order" label="排序值">
            <Input type="number" placeholder="数值越大越靠前" />
          </Form.Item>

          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item name="image_url" label="图片地址" rules={[{ required: true, message: '请上传图片' }]}>
            <Input
              placeholder="https://img2.zhilengwang.cn/..."
              addonBefore={
                <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
                  <Button icon={<UploadOutlined />}>上传</Button>
                </Upload>
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BuiltinAssetManage;
